import type { ReactNode, JSX } from "react";
import React, { isValidElement } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { Badge, LegendWithValue, PlainTooltip } from "@similarweb/ui-components";

import { InfoIcon } from "pro-features/components/BoxTitle/src/BoxTitle";
import GAVerifiedContainer from "app/components/React/GAVerifiedIcon/GAVerifiedContainer";
import {
    GridBox,
    IconsContainer,
    InfoIconContainer,
    LegendWrapperWithSeparator,
    ScrollWithFadeContainer,
} from "app/components/React/Legends/styledComponents";
import { CHART_COLORS } from "app/constants/ChartColors";
import { dynamicFilterFilter } from "app/filters/dynamicFilter";
import { FadeWrapper } from "app/UtilitiesAndConstants/UtilitiesComponents/FadeWrapper";

import type { ILegendProps } from "./legendsTypes";

const colors = CHART_COLORS.compareMainColors;
const maxWebsitesPerRow = 8;
const nameAndDataMargin = "2px";

function isJsxElement(arg: any): arg is JSX.Element {
    return isValidElement(arg);
}

function getElementOrString(reactNode: ReactNode): string | JSX.Element {
    return typeof reactNode !== "string" && isJsxElement(reactNode) ? reactNode : reactNode.toString();
}

export const Legends: React.FunctionComponent<ILegendProps> = ({
    legendItems,
    toggleSeries,
    legendsGap,
    showLegendsData = true,
    legendComponent: legendComponentProp,
    gridDirection,
    legendComponentWrapper,
    textMaxWidth = "150px",
    subtitleMaxWidth,
    //You can use it to display N/A rather than nothing when there is no value.
    displayNA = false,
    scrollContainerHeight,
}) => {
    const [dummyVar, setDummyVar] = React.useState(true);
    const reRenderLegendsComponent = () => setDummyVar(!dummyVar);
    const itemsAmount = legendItems.length;
    const legends = legendItems.map((item, index) => {
        const { customLegendComponent } = item;
        const icons = [];
        if (item.isGAVerified) {
            icons.push(
                <IconsContainer key={"GAVerified"}>
                    <GAVerifiedContainer
                        size="SMALL"
                        isActive={true}
                        isPrivate={item.isGAPrivate}
                        key={index}
                        tooltipAvailable={true}
                        tooltipIsActive={false}
                        metric={item.metric}
                    />
                </IconsContainer>,
            );
        }
        if (item.isWinner) {
            icons.push(
                <IconsContainer
                    key="winner"
                    className="winnerIcon"
                >
                    <SWReactIcons
                        iconName="winner"
                        size="xs"
                    />
                </IconsContainer>,
            );
        }
        if (item.infoTooltip) {
            icons.push(
                <InfoIconContainer key="info-tooltip">
                    <PlainTooltip
                        placement="top"
                        tooltipContent={item.infoTooltip}
                    >
                        <span>
                            <InfoIcon iconName="info" />
                        </span>
                    </PlainTooltip>
                </InfoIconContainer>,
            );
        }
        if (item.isBeta) {
            icons.push(
                <InfoIconContainer key={index}>
                    <Badge type="beta" />
                </InfoIconContainer>,
            );
        }
        const legendComponent = customLegendComponent ?? legendComponentProp;
        const value = item.data && dynamicFilterFilter()(item.data, item.format);
        const Legend: any = legendComponent ? legendComponent : LegendWithValue;
        const legend = (
            <Legend
                segmentSubtitleWidth={164}
                text={item.name}
                setOpacity={item.setOpacity}
                isChecked={item.visible ?? !item.hidden}
                subtitleText={item?.subtitleText}
                labelColor={item.color || colors[index % colors.length]}
                value={item.showLegendData !== false && showLegendsData ? value : null}
                key={index}
                icons={icons}
                textMaxWidth={textMaxWidth}
                subtitleMaxWidth={subtitleMaxWidth}
                // the distance between the website name and the value bellow
                gridRowGap={item.gridRowGap || nameAndDataMargin}
                isDisabled={item.isDisabled}
                onClick={
                    toggleSeries
                        ? (event) => {
                              toggleSeries(item, !!item.hidden, event, reRenderLegendsComponent);
                          }
                        : null
                }
                displayNA={displayNA}
                favicon={item.favicon}
                tag={item.tag}
            />
        );

        // this allows us to define both a tooltip and a separator line for the same item
        let legendTooltip = legend;

        if (item.tooltip) {
            legendTooltip = (
                <PlainTooltip
                    placement="top"
                    tooltipContent={getElementOrString(item.tooltip)}
                    key={index}
                    {...item.tooltipProps}
                >
                    <span>{legendTooltip}</span>
                </PlainTooltip>
            );
        }
        if (item.shouldPresentSeparatorAboveThisItem) {
            legendTooltip = <LegendWrapperWithSeparator>{legendTooltip}</LegendWrapperWithSeparator>;
        }

        const Wrapper = legendComponentWrapper || item.legendComponentWrapper;
        return Wrapper ? <Wrapper key={index}>{legendTooltip}</Wrapper> : legendTooltip;
    });
    const repeatAmount = Math.min(itemsAmount, maxWebsitesPerRow);
    return scrollContainerHeight ? (
        <ScrollWithFadeContainer height={scrollContainerHeight}>
            <FadeWrapper>{legends}</FadeWrapper>
        </ScrollWithFadeContainer>
    ) : (
        <GridBox
            className="legends-container"
            repeatAmount={repeatAmount.toString()}
            gap={legendsGap}
            gridDirection={gridDirection}
        >
            {legends}
        </GridBox>
    );
};
export default Legends;
